import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CalendarCheck2, Phone, Video, MapPin, Loader2 } from "lucide-react";
import { http, errText } from "@/lib/api";
import { Wordmark } from "@/components/site/Logo";

const ICON = { in_person: MapPin, phone: Phone, video: Video };

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#FBF8F1] font-sans">
    <header className="border-b border-[#154F49]/15"><div className="max-w-3xl mx-auto px-4 sm:px-6 h-20 flex items-center"><a href="/" aria-label="Elevion home"><Wordmark /></a></div></header>
    <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14 space-y-6">{children}</main>
  </div>
);

const groupByDay = (slots) => slots.reduce((acc, s) => {
  const day = s.when_label.split(" at ")[0];
  (acc[day] = acc[day] || []).push(s);
  return acc;
}, {});

const Done = ({ result }) => (
  <div className="p-6 sm:p-8 bg-white rounded-3xl border-2 border-[#154F49] space-y-3" data-testid="booking-confirmed">
    <CalendarCheck2 className="w-10 h-10 text-[#154F49]" />
    <h1 className="font-serif text-4xl text-[#154F49]">You're booked</h1>
    <p className="text-lg text-[#1A2E2B] font-semibold">{result.when_label}</p>
    <p className="text-[#465F57]">{result.mode_label}. {result.email === "sent" ? "A confirmation email with a calendar invite is on its way." : "We couldn't send your confirmation email — please call (202) 710-0131 to confirm."} We'll send a reminder the morning before.</p>
  </div>
);

export default function BookInterview() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [pick, setPick] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const load = () => http.get(`/booking/${token}`).then((r) => setInfo({ first_name: r.data?.first_name ?? "", slots: Array.isArray(r.data?.slots) ? r.data.slots : [] })).catch((e) => setError(errText(e)));
  useEffect(() => { load(); return undefined; }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const book = async () => {
    setBusy(true); setError("");
    try { const r = await http.post(`/booking/${token}`, { slot_id: pick }); setResult(r.data); }
    catch (e) { setError(errText(e)); setPick(null); load(); }
    finally { setBusy(false); }
  };

  if (result) return <Shell><Done result={result} /></Shell>;
  if (!info) return <Shell>{error ? <p className="p-6 rounded-2xl bg-red-50 text-red-900 font-medium" data-testid="booking-error">{error}</p> : <Loader2 className="w-6 h-6 animate-spin text-[#154F49]" />}</Shell>;
  const days = groupByDay(info.slots);
  return (
    <Shell>
      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#9B742A]">Interview booking</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#154F49]">Hi {info.first_name}, pick a time</h1>
        <p className="text-[#2C3B37]">All times are Eastern. Choose the slot that works best and we'll email your confirmation and calendar invite.</p>
      </div>
      {error && <p className="p-4 rounded-2xl bg-red-50 text-red-900 text-sm font-medium" data-testid="booking-error">{error}</p>}
      {info.slots.length === 0 && <p className="p-6 bg-white rounded-3xl border border-[#154F49]/15 text-[#465F57]" data-testid="booking-no-slots">No open times right now. Please call (202) 710-0131 and we'll find a time with you.</p>}
      {Object.entries(days).map(([day, list]) => (
        <section key={day} className="space-y-2">
          <h2 className="text-sm font-bold text-[#154F49]">{day}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {list.map((s) => {
              const Icon = ICON[s.mode] || MapPin;
              const on = pick === s.id;
              return (
                <button key={s.id} type="button" onClick={() => setPick(s.id)} aria-pressed={on} data-testid={`booking-slot-${s.id}`}
                  className={`text-left p-4 rounded-2xl border-2 transition-colors ${on ? "border-[#154F49] bg-[#154F49] text-[#FBF8F1]" : "border-[#154F49]/20 bg-white text-[#1A2E2B] hover:border-[#154F49]/60"}`}>
                  <span className="block font-semibold">{s.when_label.split(" at ")[1]}</span>
                  <span className={`text-sm inline-flex items-center gap-1.5 ${on ? "text-[#E2C372]" : "text-[#465F57]"}`}><Icon className="w-4 h-4" /> {s.mode_label} · {s.duration_min} min</span>
                </button>
              );
            })}
          </div>
        </section>
      ))}
      {info.slots.length > 0 && (
        <button type="button" onClick={book} disabled={!pick || busy} className="btn-primary w-full sm:w-auto justify-center text-base px-7 py-4 disabled:opacity-50" data-testid="booking-confirm-btn">
          {busy ? "Booking…" : "Confirm this time"}
        </button>
      )}
    </Shell>
  );
}
